import { ListContainer, SearchBar, Stack, pn } from "@compass/core";
import { ListFooter } from "components";
import {
  StudentViewResource,
  StudentApplicationFilter as StudentApplicationFilterModel,
  Permission,
  StudentApplicationListFilter,
  StudentApplicationListDto,
  StudentApplicationListResource,
  StudentFileListDto,
  StudentFileListFilter,
  StudentFileCategorizedListFilter,
  StudentFileCategorizedFilter,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useInitListFilter, useList, useListFilter } from "hooks";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { StudentApplicationFilter } from "pages/students/student-applications/StudentApplicationListPage/StudentApplicationFilter";
import { StudentFileTable } from "pages/students/student-files/StudentFileListPage/StudentFileTable";
import { StudentFileFilter } from "pages/students/student-files/StudentFileListPage/StudentFileFilter";

export const StudentFiles = (props: {
  initialLoad: boolean;
  studentId: number;
  activeSchoolYear: number | undefined;
  onGetList: () => void;
  viewResource: StudentViewResource;
}) => {
  const ROOT_PERMISSION: Permission = Permission.StudentApplication;
  
  const { listFilter, setListFilter } = useInitListFilter<StudentFileCategorizedListFilter>({
    sortObjects: [{ property: pn<StudentFileListDto>("createdDate"), isDesc: true }],
    fileFilter: {
      isApplication: true
    }
  });

  const [initListFilter] = useState<StudentFileCategorizedListFilter>(listFilter);
  const [listItems, setListItems] = useState<StudentFileListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentApplicationListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentFiles.getCategorizedList(listFilter, props.studentId).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.studentFiles.getCategorizedListTotal(listFilter, props.studentId).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.studentFiles.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<StudentFileCategorizedListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <StudentFileFilter
        filter={listFilter.fileFilter || ({} as StudentFileCategorizedFilter)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.fileFilter = filter;
            })
          )
        }
      />
    ),
  });

  const getIdsMethods = () => ApiService.studentApplications.getIds(listFilter);

  return (
    <Stack spacing="md" height="100%" overflow="auto">
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <StudentFileTable
              openNewTab={true}
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              studentFiles={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
    </Stack>
  );
};
